import { FC, useMemo } from 'react';
import { Order } from 'shared/types/cart';
import Typography from 'components/commercetools-ui/atoms/typography';
import { useFormat } from 'helpers/hooks/useFormat';
import useLocale from 'helpers/hooks/useLocale';

type ThankYouFooterProps = {
  loading: boolean;
  deliveryOption?: string;
  pickup: string;
  order?: Order | null;
};

const ThankYouFooter: FC<ThankYouFooterProps> = ({ deliveryOption, pickup, order }) => {
  const { formatMessage } = useFormat({ name: 'thank-you' });

  const orderCreationDateTime = useMemo(
    () => (order?.createdAt ? new Date(order.createdAt) : null),
    [order?.createdAt],
  );

  const collectionDateTime = useMemo(
    () => (orderCreationDateTime ? new Date(orderCreationDateTime.getTime() + 2 * 60 * 60 * 1000) : null),
    [orderCreationDateTime],
  );

  const formattedTime = useMemo(
    () =>
      orderCreationDateTime &&
      collectionDateTime &&
      collectionDateTime.toLocaleTimeString('en-GB', {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
      }),
    [orderCreationDateTime, collectionDateTime],
  );

  const formattedDate = useMemo(() => {
    const formatCollectionDate = (date: Date, locale: string): string => {
      const formatter = new Intl.DateTimeFormat(locale, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      });
      const parts = formatter.formatToParts(date);
      return `${parts.find((p) => p.type === 'weekday')?.value} ${parts.find((p) => p.type === 'day')?.value} ${
        parts.find((p) => p.type === 'month')?.value
      }`;
    };

    return collectionDateTime ? formatCollectionDate(collectionDateTime, 'en-GB') : '';
  }, [collectionDateTime, 'en-GB']);

  const collectionInstructions = useMemo(() => {
    if (pickup === '2 hours') {
      return (
        <div>
          <span className="font-normal">
            {formatMessage({
              id: 'collection.time.2.hour.pre',
              defaultMessage: 'Collection after:',
            })}
          </span>
          <span className="ml-4 font-normal uppercase">{formattedTime}</span>,
          <span className="mr-4 font-normal"> {formattedDate}</span>
          <span className="font-normal">
            {formatMessage({
              id: 'please.note.store.opening.time',
              defaultMessage: '(please also note our opening hours)',
            })}
          </span>
        </div>
      );
    }

    if (pickup === '2 working days') {
      return (
        <div>
          <div className="mb-12 font-normal">
            {formatMessage({
              id: 'collection.time.2.days.1',
              defaultMessage:
                'Please print out this confirmation and take it to the store, along with the card you used to pay for this order or any photo ID.',
            })}
          </div>
          <div className="mb-12 font-normal">
            {formatMessage({
              id: 'collection.time.2.days.2',
              defaultMessage:
                'Orders will be delivered to your chosen store in 2 working days (Mon - Fri, excl. bank holidays)',
            })}
          </div>
        </div>
      );
    }

    if (pickup !== '2 working days' && pickup !== '2 hours') {
      return (
        <div>
          <div className="mb-12 font-normal">
            {formatMessage({
              id: 'custom.fit.delivery.footer.1',
              defaultMessage:
                'Please print out this confirmation and take it to the store, along with the card you used to pay for this order or any photo ID.',
            })}
          </div>
          <div className="mb-12 font-normal">
            {formatMessage({
              id: 'custom.fit.delivery.footer.2',
              defaultMessage: 'Orders will be delivered to your chosen store at the estimated time schedule: ',
            })}
            <span className="font-normal">{pickup}</span>
          </div>
          <div className="mb-12 font-normal">
            {formatMessage({
              id: 'custom.fit.delivery.footer.3',
              defaultMessage:
                'You will receive notification when your order is ready to be collected from your chosen store',
            })}
          </div>
        </div>
      );
    }

    return null;
  }, [pickup, formattedTime, formattedDate, formatMessage]);

  return deliveryOption === 'StoreCollection' ? (
    <div className="w-[100%] border-t border-neutral-400 py-16">
      <Typography className="mb-7 w-fit font-semibold leading-loose text-secondary-black md:text-16">
        {formatMessage({
          id: 'print.instructionsheader',
          defaultMessage: 'This order will be available from your chosen store:',
        })}
      </Typography>
      {collectionInstructions}
    </div>
  ) : null;
};

export default ThankYouFooter;
